import React from 'react';
import { useTranslation } from 'react-i18next';
import { Users, FolderTree, ShoppingBag, Package } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import { LIVE_POLL, livePollInterval } from '../config/liveSync';
import { adminKeys } from '../lib/queryKeys';

interface DashboardStats {
  totalUsers: number;
  totalProducts: number;
  totalOrders: number;
  totalCategories: number;
  totalBrands: number;
}

interface RecentProduct {
  id: string;
  name: string;
  brand: string;
  price?: number;
  isActive: boolean;
  createdAt: string;
}

interface Category {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
}

const Dashboard: React.FC = () => {
  const { t } = useTranslation();

  const { data: dashboardData, isLoading: loading } = useQuery({
    queryKey: adminKeys.dashboard(),
    queryFn: async () => {
      try {
        // Fetch all data in parallel
        const [
          usersResponse,
          productsResponse,
          ordersResponse,
          categoriesResponse,
          brandsResponse
        ] = await Promise.all([
          api.get('/admin/users?limit=1'), // Just get count
          api.get('/admin/products?limit=1'), // Just get count
          api.get('/admin/orders?limit=1'), // Just get count
          api.get('/admin/categories'),
          api.get('/admin/brands')
        ]);

        const stats: DashboardStats = {
          totalUsers: usersResponse.data.data.total || 0,
          totalProducts: productsResponse.data.data.total || 0,
          totalOrders: ordersResponse.data.data.total || 0,
          totalCategories: categoriesResponse.data.data.length || 0,
          totalBrands: brandsResponse.data.data.length || 0,
        };

        // Get recent products (first 5)
        const recentProducts: RecentProduct[] = productsResponse.data.data.products
          ? productsResponse.data.data.products.slice(0, 5)
          : [];

        // Get categories (first 6)
        const categories: Category[] = categoriesResponse.data.data
          ? categoriesResponse.data.data.slice(0, 6)
          : [];

        return { stats, recentProducts, categories };
      } catch (error: any) {
        // Only log network errors if they're not connection refused (server not running)
        if (error.code !== 'ERR_NETWORK' && error.code !== 'ECONNREFUSED') {
          console.error('Error fetching dashboard data:', error);
        }
        // Return default values on error
        return {
          stats: {
            totalUsers: 0,
            totalProducts: 0,
            totalOrders: 0,
            totalCategories: 0,
            totalBrands: 0,
          },
          recentProducts: [],
          categories: [],
        };
      }
    },
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: () => livePollInterval(LIVE_POLL.STATS),
    placeholderData: (previous) => previous,
  });

  const stats = dashboardData?.stats || {
    totalUsers: 0,
    totalProducts: 0,
    totalOrders: 0,
    totalCategories: 0,
    totalBrands: 0,
  };
  const recentProducts = dashboardData?.recentProducts || [];
  const categories = dashboardData?.categories || [];

  const dashboardStats = [
    {
      icon: Users,
      labelKey: 'stats.totalUsers',
      value: loading ? '...' : stats.totalUsers.toLocaleString(),
      change: '+12%',
      changeType: 'increase',
      color: 'text-primary-600',
      bgColor: 'bg-primary-50',
    },
    {
      icon: ShoppingBag,
      labelKey: 'stats.activeProducts',
      value: loading ? '...' : stats.totalProducts.toLocaleString(),
      change: '+15%',
      changeType: 'increase',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      icon: Package,
      labelKey: 'stats.totalOrders',
      value: loading ? '...' : stats.totalOrders.toLocaleString(),
      change: '+28%',
      changeType: 'increase',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      icon: FolderTree,
      labelKey: 'stats.totalCategories',
      value: loading ? '...' : stats.totalCategories.toLocaleString(),
      change: '+8%',
      changeType: 'increase',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">{t('title', { ns: 'dashboard' })}</h1>
        <p className="text-gray-600 mt-2">{t('subtitle', { ns: 'dashboard' })}</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {dashboardStats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={stat.labelKey || `stat-${index}`} className="card p-6">
              <div className="flex items-center">
                <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                  <Icon className={`w-6 h-6 ${stat.color}`} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{t(stat.labelKey, { ns: 'dashboard' })}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
              </div>
              <div className="mt-4">
                <span className={`text-sm font-medium ${
                  stat.changeType === 'increase' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {stat.change}
                </span>
                <span className="text-sm text-gray-500 ml-2">{t('fromLastMonth', { ns: 'dashboard' })}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Products */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('recentActivity', { ns: 'dashboard' })}</h3>
          <div className="space-y-4">
            {loading ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
              </div>
            ) : recentProducts.length > 0 ? (
              recentProducts.map((product, index) => (
                <div key={product.id || `product-${index}`} className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <ShoppingBag size={16} className="text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{product.name}</p>
                    <p className="text-xs text-gray-500">{product.brand} • {product.price ? `$${product.price.toFixed(2)}` : 'Variable pricing'}</p>
                  </div>
                  <span className={`badge-${product.isActive ? 'green' : 'red'}`}>
                    {t(product.isActive ? 'status.active' : 'status.inactive', { ns: 'common' })}
                  </span>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 py-4">{t('table.noData', { ns: 'common' })}</div>
            )}
          </div>
          <div className="mt-4">
            <Link to="/products" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
              {t('menu.products', { ns: 'navigation' })} →
            </Link>
          </div>
        </div>

        {/* Categories Overview */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('menu.categories', { ns: 'navigation' })}</h3>
          <div className="grid grid-cols-2 gap-3">
            {loading ? (
              <div className="col-span-2 flex justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
              </div>
            ) : categories.length > 0 ? (
              categories.map((category, index) => (
                <div key={category.id || `category-${index}`} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <FolderTree size={14} className="text-orange-600" />
                      <span className="text-sm font-medium text-gray-900 truncate">{category.name}</span>
                    </div>
                    <span className={`w-2 h-2 rounded-full ${category.isActive ? 'bg-green-500' : 'bg-red-500'}`}></span>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-2 text-center text-gray-500 py-4">{t('table.noData', { ns: 'common' })}</div>
            )}
          </div>
          <div className="mt-4">
            <Link to="/categories" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
              {t('menu.categories', { ns: 'navigation' })} →
            </Link>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('quickActions', { ns: 'dashboard' })}</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link to="/products" className="btn-primary text-center py-4 no-underline">
            {t('menu.products', { ns: 'navigation' })}
          </Link>
          <Link to="/orders" className="btn-secondary text-center py-4 no-underline">
            {t('menu.orders', { ns: 'navigation' })}
          </Link>
          <Link to="/users" className="btn-outline text-center py-4 no-underline">
            {t('menu.users', { ns: 'navigation' })}
          </Link>
          <Link to="/categories" className="btn-outline text-center py-4 no-underline">
            {t('menu.categories', { ns: 'navigation' })}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;